/**
 * ⟦A-15 §1⟧ **Les idées expirent** — la première des quatre pressions du temps.
 *
 * Mot de Mathieu : *« Une carte d'inspiration non jouée se périme — du coup ça enlève, donc
 * tu es obligé de refaire des recherches. »* La veille cesse d'être une **corvée de recharge**
 * pour devenir un **entretien** : on ne remplit plus une main une fois pour toutes, on la
 * tient à jour. Traîner, c'est perdre sa main et devoir tout refaire.
 *
 * ── Prévenir, jamais surprendre (PLAN, garde-fou d'A-15) ───────────────────────
 *
 * Une idée qui expire **se voit venir**. Ce module ne se contente donc pas de retirer des
 * cartes : il expose `fraicheurCarte`, qui rend les heures restantes et une bande lisible
 * (`fraiche` / `tiede` / `bientot_perimee`) que l'UI affiche **en permanence** sur chaque
 * carte. La péremption n'a pas besoin d'un compte à rebours anxiogène — il suffit que le
 * joueur puisse lire, à tout instant, laquelle de ses idées est en train de refroidir.
 *
 * ── L'état neutre par défaut (CLAUDE.md, ⟦A-10⟧) ────────────────────────────────
 *
 * Une carte sans `expires_h` **ne se périme jamais**. C'est vrai des états déjà sérialisés
 * (aucune migration), des fixtures de test, et — délibérément — de la **main de départ** :
 * tant que l'escalier ⟦A-5⟧ n'a pas ouvert la veille, le joueur n'a aucun moyen de retirer
 * une idée, et la lui retirer serait un soft-lock. A-15 présuppose l'inverse : « tu es obligé
 * de refaire des recherches » n'a de sens que si refaire des recherches est possible.
 *
 * Pureté (CLAUDE.md) : aucune I/O, aucune horloge système, aucun `Math.random`.
 */
import { H_JOUR } from "./clock.js";
import { mecaniqueOuverte } from "./escalier.js";
// ─────────────────────────────────────────────────────────────────────────────
// Constantes normatives
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ✎ **Durée de vie d'une idée : 80 h simulées.**
 *
 * A-15 laisse le chiffre en ✎ et pose le critère : *« trop court elle harcèle, trop long elle
 * ne mord pas »*. Il a été **mesuré** contre la nouvelle échelle de temps ⟦A-14⟧, où une
 * longue coûte 48 h et une evergreen 64 h — c'est cette échelle qui décide, pas l'intuition :
 *
 * | durée de vie | idées perdues / 60 j | ce que ça produit |
 * |---|---|---|
 * | 48 h (3 j) | harcèlement : une longue (48 h) périme à elle seule toute la main |
 * | **80 h (5 j)** | **la main tourne, une longue ne coûte jamais toute sa réserve** |
 * | 112 h (1 sem.) | mord à peine : on stocke encore 6 idées sans y penser |
 *
 * Le repère qui tranche : **une idée doit survivre à une longue, jamais à deux.** 80 h laisse
 * produire une longue (48 h) puis une veille et un short ; deux longues d'affilée (96 h) la
 * font tomber. C'est exactement le geste que l'avenant veut rendre coûteux.
 *
 * ⚠️ ⟦A-19⟧ **Cette durée est calée sur `H_LONGUE`, PAS sur la journée** — c'est la seule du
 * moteur dans ce cas, et c'est pourquoi elle ne se rééchelonne pas avec `H_JOUR`. Le repère
 * ci-dessus est un encadrement par les COÛTS D'ACTION (48 h < 80 h < 96 h), et les coûts
 * d'action sont en heures absolues ⟦A-14⟧ : les rééchelonner casserait l'encadrement au lieu
 * de le préserver. Écrit `5 * H_JOUR` jusqu'ici, ce qui donnait 80 h par coïncidence
 * arithmétique — et donnerait 120 h aujourd'hui, soit une idée qui survit à deux longues.
 * En jours, elle vaut désormais 3,3 au lieu de 5 : c'est le même geste, dans une journée
 * plus longue. `H_LONGUE` n'est pas importable ici (`actions.ts` importe déjà ce module —
 * cycle), d'où le littéral, et le test `temps.test.ts` qui verrouille l'encadrement.
 */
export const DUREE_VIE_CARTE_H = 80; // ✎ — voir l'encadrement par `H_LONGUE` ci-dessus
/**
 * ✎ Seuil de la bande « bientôt périmée » : **un jour simulé restant**. En dessous, l'UI
 * marque la carte. Au-dessus de `SEUIL_TIEDE_H`, elle est fraîche et ne dit rien — on ne veut
 * pas d'une main entière qui clignote.
 */
export const SEUIL_BIENTOT_PERIMEE_H = H_JOUR; // 24 h ✎
/** ✎ Seuil de la bande « tiède » : **deux jours simulés restants**. */
export const SEUIL_TIEDE_H = 2 * H_JOUR; // 48 h ✎
/**
 * Fraîcheur d'une idée à l'instant `clockH` (défaut : maintenant). **C'est le télégraphe** :
 * appelable à tout moment, sans jouer d'action, pour que rien ne surprenne.
 */
export function fraicheurCarte(state, carte, clockH = state.clock_h) {
    const fin = carte.expires_h;
    if (fin === undefined || fin === null) {
        return { bande: "eternelle", heures_restantes: null, jours_restants: null };
    }
    const restantes = fin - clockH;
    if (restantes <= 0)
        return { bande: "perimee", heures_restantes: 0, jours_restants: 0 };
    const bande = restantes <= SEUIL_BIENTOT_PERIMEE_H
        ? "bientot_perimee"
        : restantes <= SEUIL_TIEDE_H
            ? "tiede"
            : "fraiche";
    return { bande, heures_restantes: restantes, jours_restants: Math.floor(restantes / H_JOUR) };
}
/** L'idée est-elle périmée à `clockH` ? Une carte sans échéance ne l'est jamais. */
export function cartePerimee(state, carte, clockH = state.clock_h) {
    return fraicheurCarte(state, carte, clockH).bande === "perimee";
}
/** La main, annotée de sa fraîcheur — la vue dont l'écran d'inspiration a besoin. */
export function mainAnnotee(state) {
    return state.inspiration.map((carte) => ({ carte, fraicheur: fraicheurCarte(state, carte) }));
}
/**
 * L'échéance à stamper sur une carte tirée **maintenant** — `null` si la péremption ne
 * s'applique pas encore.
 *
 * ⚠️ **Le garde-fou anti-soft-lock est ici, et il est structurel.** Tant que l'escalier ⟦A-5⟧
 * n'a pas ouvert la **veille**, le joueur ne dispose d'aucun moyen de renouveler sa main :
 * une idée qui se périmerait le laisserait sans rien à produire, et `veille` lui serait
 * refusée en `mecanique_verrouillee`. On ne pose donc pas d'échéance — pas par prudence, mais
 * parce que la règle d'A-15 (« tu es obligé de refaire des recherches ») n'a littéralement
 * pas de sens avant que la recherche existe.
 */
export function echeanceCarte(state) {
    if (!mecaniqueOuverte(state, "veille"))
        return null;
    return state.clock_h + DUREE_VIE_CARTE_H;
}
// ─────────────────────────────────────────────────────────────────────────────
// La purge — appelée par la file §2.3 à chaque avancée d'horloge
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Retire de la main les idées périmées. MUTE `state` (convention de `curves.ts` et
 * `sleep.ts` : on travaille sur le clone de la file §2.3). Rend les cartes retirées.
 *
 * **Appelée après chaque avancée d'horloge**, pas seulement au sommeil : une longue de 48 h
 * ⟦A-14⟧ doit pouvoir périmer des idées, c'est précisément la tension qu'A-15 cherche. Une
 * mécanique qui n'agirait qu'au sommeil laisserait le joueur produire trois jours durant sans
 * que son stock ne vieillisse.
 */
export function purgerCartesPerimees(state, events = [], log = []) {
    const perimees = state.inspiration.filter((c) => cartePerimee(state, c));
    if (perimees.length === 0)
        return [];
    state.inspiration = state.inspiration.filter((c) => !cartePerimee(state, c));
    events.push({
        kind: "warning",
        data: {
            scripted: "carte_perimee",
            cartes: perimees.length,
            sujets: perimees.map((c) => c.subject_id).join(","),
            main_restante: state.inspiration.length,
        },
    });
    log.push({
        turn: state.turn,
        clock_h: state.clock_h,
        famille: "quotidien",
        text: perimees.length === 1
            ? "Une idée a fait son temps : plus personne n'en parle. Il va falloir retourner chercher."
            : `${perimees.length} idées ont fait leur temps. Le sujet est passé — retour à la veille.`,
    });
    return perimees;
}
//# sourceMappingURL=idees.js.map